import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import {
  AcademyRealtimeState,
  HubMuteChange,
  HubQuestionPatch,
  initialAcademyRealtimeState,
} from './entities/academy-realtime-store.model';
import {
  ChatMessageAddedEvent,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  QuestionAddedEvent,
} from './entities/academy-realtime.model';
import { LiveQuestionStatusType, ReactionType } from './entities/academy-enums.model';
import { AcademyRealtimeService } from './infra/academy-realtime.service';

function append<T>(byKey: Record<string, T[]>, key: string, item: T): Record<string, T[]> {
  return { ...byKey, [key]: [...(byKey[key] ?? []), item] };
}

function patchQuestion(
  byEvent: Record<string, QuestionAddedEvent[]>,
  liveEventId: string,
  questionId: string,
  patch: Partial<QuestionAddedEvent>,
): Record<string, QuestionAddedEvent[]> {
  const list = byEvent[liveEventId] ?? [];
  return {
    ...byEvent,
    [liveEventId]: list.map((q) => (q.questionId === questionId ? { ...q, ...patch } : q)),
  };
}

/** Latest roster event per attendance session: a later event for the same session replaces it. */
function upsertParticipant(
  byEvent: Record<string, Record<string, ParticipantJoinedEvent | ParticipantLeftEvent>>,
  event: ParticipantJoinedEvent | ParticipantLeftEvent,
): Record<string, Record<string, ParticipantJoinedEvent | ParticipantLeftEvent>> {
  return {
    ...byEvent,
    [event.liveEventId]: { ...(byEvent[event.liveEventId] ?? {}), [event.liveAttendanceId]: event },
  };
}

/** Records the latest hub value(s) for a question, merged over what was already recorded. */
function recordQuestionPatch(
  byEvent: Record<string, Record<string, HubQuestionPatch>>,
  liveEventId: string,
  questionId: string,
  patch: HubQuestionPatch,
): Record<string, Record<string, HubQuestionPatch>> {
  const forEvent = byEvent[liveEventId] ?? {};
  return {
    ...byEvent,
    [liveEventId]: { ...forEvent, [questionId]: { ...(forEvent[questionId] ?? {}), ...patch } },
  };
}

/** Records a removed chat message id once. */
function recordRemovedChat(
  byEvent: Record<string, string[]>,
  liveEventId: string,
  chatMessageId: string,
): Record<string, string[]> {
  const ids = byEvent[liveEventId] ?? [];
  return ids.includes(chatMessageId) ? byEvent : { ...byEvent, [liveEventId]: [...ids, chatMessageId] };
}

/** Copy of a per-event record without that event's entry. */
function withoutEvent<T>(byEvent: Record<string, T>, liveEventId: string): Record<string, T> {
  const rest = { ...byEvent };
  delete rest[liveEventId];
  return rest;
}

function removeChat(
  byEvent: Record<string, ChatMessageAddedEvent[]>,
  liveEventId: string,
  chatMessageId: string,
): Record<string, ChatMessageAddedEvent[]> {
  const list = byEvent[liveEventId] ?? [];
  return { ...byEvent, [liveEventId]: list.filter((m) => m.chatMessageId !== chatMessageId) };
}

/**
 * Adapts `AcademyRealtimeService` streams into signals and exposes the hub's
 * client→server commands. Subscribes to every stream in `onInit` and mirrors it
 * into in-memory state (no persistence — FR-030).
 */
export const AcademyRealtimeStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyRealtimeState>(initialAcademyRealtimeState),
  withMethods((store, service = inject(AcademyRealtimeService)) => ({
    connect: (): Promise<void> => service.start(),
    disconnect: (): Promise<void> => service.stop(),
    joinEvent: (liveEventId: string): Promise<void> => {
      patchState(store, {
        connectedEventId: liveEventId,
        joinRejected: false,
        rejoinRejected: false,
      });
      return service.joinEvent(liveEventId);
    },
    /**
     * Also drops the event's recorded vote/status values and removed ids: events keep flowing
     * while the viewer is away, so on return the fresh REST read is the truth and an old
     * recorded value must not override it.
     */
    leaveEvent: (liveEventId: string, watchedSeconds: number): Promise<void> => {
      patchState(store, {
        connectedEventId: null,
        questionPatchesByEvent: withoutEvent(store.questionPatchesByEvent(), liveEventId),
        removedChatIdsByEvent: withoutEvent(store.removedChatIdsByEvent(), liveEventId),
      });
      return service.leaveEvent(liveEventId, watchedSeconds);
    },
    /** Producer studio join (backend 013): no attendance, not counted as a viewer. */
    joinAsProducer: (liveEventId: string): Promise<void> => {
      patchState(store, {
        connectedEventId: liveEventId,
        joinRejected: false,
        rejoinRejected: false,
      });
      return service.joinAsProducer(liveEventId);
    },
    /**
     * Leaves the producer groups and drops what the studio received for that event: the
     * producers group delivers every tenant's Q&A/chat, which must not remain for a viewer
     * page of the same live opened later in the session.
     */
    leaveAsProducer: (liveEventId: string): Promise<void> => {
      patchState(store, {
        connectedEventId: null,
        questionsByEvent: withoutEvent(store.questionsByEvent(), liveEventId),
        chatByEvent: withoutEvent(store.chatByEvent(), liveEventId),
        participantsByEvent: withoutEvent(store.participantsByEvent(), liveEventId),
        questionPatchesByEvent: withoutEvent(store.questionPatchesByEvent(), liveEventId),
        patchesAtProducerRejoinByEvent: withoutEvent(
          store.patchesAtProducerRejoinByEvent(),
          liveEventId,
        ),
        removedChatIdsByEvent: withoutEvent(store.removedChatIdsByEvent(), liveEventId),
        muteChangesByEvent: withoutEvent(store.muteChangesByEvent(), liveEventId),
      });
      return service.leaveAsProducer(liveEventId);
    },
    /**
     * The studio's muted-list read landed (called by `AcademyQnaChatStore`). `seenChanges` is
     * `muteChangeCount` as it was when the read went out: every change recorded up to there had
     * already been applied on the server, so the read carries it and it is dropped. One that
     * arrived while the read was in flight is not in it, and stays to be applied over it.
     */
    mutedRead: (liveEventId: string, seenChanges: number): void => {
      const kept: Record<string, HubMuteChange> = {};
      for (const [personId, change] of Object.entries(
        store.muteChangesByEvent()[liveEventId] ?? {},
      )) {
        if (change.order > seenChanges) kept[personId] = change;
      }
      patchState(store, {
        muteChangesByEvent: { ...store.muteChangesByEvent(), [liveEventId]: kept },
      });
    },
    /**
     * The studio's producer question re-read landed (called by `AcademyQnaChatStore` with the
     * rows it just stored): what the re-join snapshotted is now carried by the read, so it is
     * dropped from the record — kept, a vote of 3 would be applied over a read that says 7.
     *
     * Three things narrow the drop, and each one is a regression that was seen:
     *
     * - **only questions the read returned.** For one it does not carry (asked over the hub,
     *   not yet in the read) it supersedes nothing, and dropping would fall back to the count
     *   `QuestionAdded` arrived with.
     * - **only values unchanged since the re-join.** One the hub delivered while the read was
     *   in flight is not in it — the read's snapshot is older — so it stays.
     *
     * Status and count follow the very same two rules. An earlier version kept a recorded
     * status whenever the read still said `Pending`, on the belief that a moderation never
     * undoes itself. It does: `PATCH /LiveQuestions/{id}/status` binds the whole enum,
     * `Pending` included, and writes and broadcasts it like any other value. So a read saying
     * `Pending` can perfectly well be a question reopened during the drop — and treating it as
     * a stale replica pinned the old `Dismissed` over the fresher read, hiding the question
     * until the producer left the studio. The snapshot comparison is what keeps the re-join
     * blocker fixed; the status needs no rule of its own on top of it.
     *
     * Nothing is dropped when the read fails or is still in flight: this is the only caller,
     * and the panel keeps showing what it had until the read actually lands.
     */
    producerQuestionsRead: (
      liveEventId: string,
      read: readonly { LiveQuestionId: string; LiveQuestionStatusType: LiveQuestionStatusType }[],
    ): void => {
      const atRejoin = store.patchesAtProducerRejoinByEvent()[liveEventId];
      if (!atRejoin) return;
      const readStatusById = new Map(
        read.map((q) => [q.LiveQuestionId, q.LiveQuestionStatusType] as const),
      );
      const kept: Record<string, HubQuestionPatch> = {};
      for (const [questionId, patch] of Object.entries(
        store.questionPatchesByEvent()[liveEventId] ?? {},
      )) {
        const before = atRejoin[questionId];
        const readStatus = readStatusById.get(questionId);
        const keepCount =
          patch.upvoteCount !== undefined &&
          !(readStatus !== undefined && patch.upvoteCount === before?.upvoteCount);
        const keepStatus =
          patch.status !== undefined &&
          !(readStatus !== undefined && patch.status === before?.status);
        if (!keepCount && !keepStatus) continue;
        kept[questionId] = {
          ...(keepCount ? { upvoteCount: patch.upvoteCount } : {}),
          ...(keepStatus ? { status: patch.status } : {}),
        };
      }
      patchState(store, {
        questionPatchesByEvent: { ...store.questionPatchesByEvent(), [liveEventId]: kept },
        patchesAtProducerRejoinByEvent: withoutEvent(
          store.patchesAtProducerRejoinByEvent(),
          liveEventId,
        ),
      });
    },
    heartbeat: (liveEventId: string, watchedSeconds: number): Promise<void> =>
      service.heartbeat(liveEventId, watchedSeconds),
    sendMessage: (liveEventId: string, text: string): Promise<void> =>
      service.sendMessage(liveEventId, text),
    sendReaction: (liveEventId: string, reactionType: ReactionType): Promise<void> =>
      service.sendReaction(liveEventId, reactionType),
    reset: (): void => patchState(store, initialAcademyRealtimeState),
  })),
  withHooks({
    onInit(store) {
      const service = inject(AcademyRealtimeService);
      const destroyRef = inject(DestroyRef);
      // Q&A/chat events count only for the joined event. `connectedEventId` is set by the
      // join calls and cleared by the leave calls and a rejected join; a reconnect keeps it,
      // so events after reconnecting still apply, while one still in flight for an event
      // already left is dropped instead of refilling its buffers.
      const joined = <T extends { liveEventId: string }>() =>
        filter((e: T) => e.liveEventId === store.connectedEventId());

      service.hubStatus$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((status) => patchState(store, { status }));

      service.eventStatusChanged$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            eventStatusById: { ...store.eventStatusById(), [e.liveEventId]: e.status },
          }),
        );

      service.viewerCountChanged$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            viewerCountByEvent: { ...store.viewerCountByEvent(), [e.liveEventId]: e.count },
          }),
        );

      service.questionAdded$
        .pipe(joined(), takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            questionsByEvent: append(store.questionsByEvent(), e.liveEventId, e),
          }),
        );

      service.questionUpvoted$
        .pipe(joined(), takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            questionsByEvent: patchQuestion(
              store.questionsByEvent(),
              e.liveEventId,
              e.questionId,
              { upvoteCount: e.upvoteCount },
            ),
            questionPatchesByEvent: recordQuestionPatch(
              store.questionPatchesByEvent(),
              e.liveEventId,
              e.questionId,
              { upvoteCount: e.upvoteCount },
            ),
          }),
        );

      service.questionStatusChanged$
        .pipe(joined(), takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            questionsByEvent: patchQuestion(
              store.questionsByEvent(),
              e.liveEventId,
              e.questionId,
              { status: e.status },
            ),
            questionPatchesByEvent: recordQuestionPatch(
              store.questionPatchesByEvent(),
              e.liveEventId,
              e.questionId,
              { status: e.status },
            ),
          }),
        );

      service.chatMessageAdded$
        .pipe(joined(), takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            chatByEvent: append(store.chatByEvent(), e.liveEventId, e),
          }),
        );

      service.chatMessageRemoved$
        .pipe(joined(), takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            chatByEvent: removeChat(store.chatByEvent(), e.liveEventId, e.chatMessageId),
            removedChatIdsByEvent: recordRemovedChat(
              store.removedChatIdsByEvent(),
              e.liveEventId,
              e.chatMessageId,
            ),
          }),
        );

      // A producer re-join makes the studio re-read its backlogs, and the re-read is the truth
      // for what happened during the drop — so the values recorded before it must not be
      // applied over it (3 votes over a read that says 7). Nothing is dropped here, though:
      // the re-join only **snapshots** them, and `producerQuestionsRead` does the dropping when
      // that read lands. Dropping here would show the recorded moderation undone for the whole
      // round trip — and for good, if the read fails.
      // Removed chat ids are not even snapshotted: a removal is never undone, and without the
      // record a message read before the drop would come back if the chat re-read fails.
      // A viewer re-join snapshots nothing — the watch page does not re-read.
      // A re-join that went through also puts the room back: the connection is in the group
      // again, so the event is joined again and no rejection stands. Without this write, a
      // `connectedEventId` cleared while the connection was down — by a rejection of the very
      // join that the drop interrupted — stayed null through the recovery, and the `joined()`
      // filter below silently dropped every question, vote, status and chat message from then
      // on, behind a banner saying the viewer is not in the audience.
      // Only when the store is not already in another room: a re-join for the previous live can
      // resolve after the viewer opened the next one, and must not pull it back.
      service.rejoined$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((rejoin) =>
          patchState(store, {
            lastRejoin: rejoin,
            ...(store.connectedEventId() === null ||
            store.connectedEventId() === rejoin.liveEventId
              ? {
                  connectedEventId: rejoin.liveEventId,
                  joinRejected: false,
                  rejoinRejected: false,
                }
              : {}),
            ...(rejoin.asProducer
              ? {
                  patchesAtProducerRejoinByEvent: {
                    ...store.patchesAtProducerRejoinByEvent(),
                    [rejoin.liveEventId]:
                      store.questionPatchesByEvent()[rejoin.liveEventId] ?? {},
                  },
                }
              : {}),
          }),
        );

      service.reactionBroadcast$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((e) => patchState(store, { lastReaction: e }));

      service.reactionCountsChanged$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            reactionCountsByEvent: {
              ...store.reactionCountsByEvent(),
              [e.liveEventId]: e.counts,
            },
          }),
        );

      service.participantJoined$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            participantsByEvent: upsertParticipant(store.participantsByEvent(), e),
          }),
        );

      service.participantLeft$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((e) =>
          patchState(store, {
            participantsByEvent: upsertParticipant(store.participantsByEvent(), e),
          }),
        );

      // Sent to the producers group only, so it reaches the studio and nothing else.
      service.chatParticipantMuteChanged$
        .pipe(joined(), takeUntilDestroyed(destroyRef))
        .subscribe((e) => {
          const order = store.muteChangeCount() + 1;
          patchState(store, {
            muteChangeCount: order,
            muteChangesByEvent: {
              ...store.muteChangesByEvent(),
              [e.liveEventId]: {
                ...(store.muteChangesByEvent()[e.liveEventId] ?? {}),
                [e.personId]: { isMuted: e.isMuted, order },
              },
            },
          });
        });

      // A chat message that did not go out is kept for the page to announce. Not filtered by
      // the joined event: a message typed on a page whose join was refused is lost all the
      // same, and the page that sent it compares the event id itself.
      service.sendRejected$
        .pipe(
          filter((e) => e.kind === 'message'),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe((e) => patchState(store, { chatSendRejection: e }));

      // Only a rejection of the event currently joined counts: a late rejection of a previous
      // join (the user already moved to another live) must not disconnect the new one.
      service.joinRejected$
        .pipe(
          filter((r) => r.liveEventId === store.connectedEventId()),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => patchState(store, { joinRejected: true, connectedEventId: null }));

      // A join the connection cut short is recorded, and nothing else: `connectedEventId` stays,
      // because the failure is the connection dropping mid-invoke — on the first join as much as
      // on a re-join — and the next reconnect recovers the group. Clearing it here is what left
      // the page deaf for good. It does not raise `joinRejected` either — that one means a gate
      // refused the viewer, and the watch page's banner says exactly that.
      service.rejoinRejected$
        .pipe(
          filter((r) => r.liveEventId === store.connectedEventId()),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => patchState(store, { rejoinRejected: true }));
    },
  }),
);
